import React, { useEffect, useRef, useState } from "react";
import {
  Box, Typography, MenuItem, Grid, ToggleButtonGroup, 
  ToggleButton, Button, CircularProgress, Collapse, Stack, Divider
} from "@mui/material"; // MUI 7 এ Grid সরাসরি আধুনিক Grid2 কে নির্দেশ করে
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { blobToBase64 } from "../../../../../../utils/blob";
import { QK } from "../../../../../../base/qk";
import { submitCarePlanReview, getCarePlanReviewsByClient } from "../../../../../../api/services/CarePlan/review";
import { useParams } from "react-router-dom";
import { CustomInput, SignatureFieldBase64 } from "../../../../../CustomForm";
import CustomDate from "../../../../../CustomForm/CustomDate";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import ConfirmModal from "../../../../../ConfirmModal";

const CarePlanReviewSection = ({ responses, handleChange, carePlanID }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { clientID } = useParams();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const hasLoadedData = useRef(false);
  const initialDateSet = useRef(false);

  const dependencyLevels = [
    { id: "INDEPENDENT", value: "Independent" },
    { id: "MINIMAL", value: "Minimal assistance and prompting" },
    { id: "LIMITED", value: "Has limited ability without assistance" },
    { id: "GREAT_DIFF", value: "Has great difficulty without assistance" },
    { id: "DEPENDENT", value: "Completely dependent" },
  ];

  const R_KEYS = {
    STATUS: "rev_care_plan_status",
    LEVEL: "rev_dependency_level",
    NEXT_DATE: "rev_next_review_date",
    EVAL_SUMMARY: "rev_evaluation_summary",
    GEN_SUMMARY: "rev_summary",
    CONSENT: "rev_consent_person_name",
    ROLE: "rev_role_how_given",
    SIG: "rev_review_signature",
    ID: "rev_id"
  };

  const { isLoading: isHistoryLoading } = useQuery(
    [QK.carePlan, "review-history", clientID],
    () => getCarePlanReviewsByClient(clientID, carePlanID),
    {
      onSuccess: (data) => {
        if (data?.[0] && !hasLoadedData.current) {
          const latest = data[0]; 
          handleChange(R_KEYS.ID, latest.id);
          handleChange(R_KEYS.STATUS, latest.care_plan_status);
          handleChange(R_KEYS.LEVEL, latest.dependency_level);
          handleChange(R_KEYS.NEXT_DATE, latest.next_review_date);
          handleChange(R_KEYS.EVAL_SUMMARY, latest.evaluation_summary);
          handleChange(R_KEYS.GEN_SUMMARY, latest.summary);
          handleChange(R_KEYS.CONSENT, latest.consent_person_name);
          handleChange(R_KEYS.ROLE, latest.role_how_given);
          handleChange(R_KEYS.SIG, latest.review_signature);
          
          hasLoadedData.current = true;
          if (latest.next_review_date) initialDateSet.current = true;
        }
      },
      enabled: !!clientID,
      refetchOnWindowFocus: false
    }
  );

  useEffect(() => {
    if (!isHistoryLoading && !initialDateSet.current && !responses[R_KEYS.NEXT_DATE]) {
      const futureDate = dayjs().add(6, "month").format("YYYY-MM-DD");
      handleChange(R_KEYS.NEXT_DATE, futureDate);
      initialDateSet.current = true; 
    }
  }, [isHistoryLoading, responses[R_KEYS.NEXT_DATE]]);

  const mutation = useMutation((payload) => submitCarePlanReview(payload), {
    onSuccess: () => {
      enqueueSnackbar("Review sync completed", { variant: "success" });
      queryClient.invalidateQueries([QK.carePlan]);
      setOpenConfirm(false);
    }
  });

  const handleFinalSubmit = () => {
    const payload = {
      id: responses[R_KEYS.ID],
      care_plan_status: responses[R_KEYS.STATUS],
      care_plan: carePlanID,
      dependency_level: responses[R_KEYS.LEVEL],
      next_review_date: responses[R_KEYS.NEXT_DATE],
      evaluation_summary: responses[R_KEYS.EVAL_SUMMARY],
      summary: responses[R_KEYS.GEN_SUMMARY],
      consent_person_name: responses[R_KEYS.CONSENT],
      role_how_given: responses[R_KEYS.ROLE],
      review_signature: responses[R_KEYS.SIG],
      client: clientID,
      is_signed: !!responses[R_KEYS.SIG],
    };
    mutation.mutate(payload);
  };

  return (
    <Box sx={{ border: '1px solid #f1f5f9', borderRadius: '16px', bgcolor: '#fff', overflow: 'hidden' }}>
      <Box 
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{ 
          p: 1.8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: '#fff', borderRadius: isExpanded ? '12px 12px 0 0' : '12px',
          border: '1px solid', borderColor: isExpanded ? '#e2e8f0' : '#f1f5f9', 
          transition: '0.2s ease-in-out',
          '&:hover': { borderColor: '#cbd5e1', bgcolor: '#fcfcfd' }
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 4, height: 24, borderRadius: '4px', bgcolor: isExpanded ? '#6366f1' : '#e2e8f0', transition: '0.3s' }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
              Care Plan Review Summary
            </Typography>
            {!isExpanded && responses[R_KEYS.STATUS] ? (
              <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Status: <span style={{ color: '#10b981', fontWeight: 700 }}>{responses[R_KEYS.STATUS]}</span>
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                Click to view or update review details
              </Typography>
            )}
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', color: isExpanded ? '#1e293b' : '#94a3b8' }}>
          {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Divider sx={{ opacity: 0.6 }} />
        <Box sx={{ p: 2.5 }}>
          {isHistoryLoading ? (
            <Box sx={{ textAlign: "center", py: 2 }}><CircularProgress size={20} /></Box>
          ) : (
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', mb: 1, display: 'block' }}>
                  PLAN STATUS
                </Typography>
                <ToggleButtonGroup
                  value={responses[R_KEYS.STATUS] || ""}
                  exclusive
                  onChange={(e, val) => val && handleChange(R_KEYS.STATUS, val)}
                  fullWidth size="small"
                  sx={{ 
                      height: 38,
                      '& .MuiToggleButton-root': { 
                          border: '1px solid #eef2f6', textTransform: 'none', fontWeight: 800, fontSize: '0.75rem',
                          '&.Mui-selected': {
                              color: '#fff',
                              '&[value="ACTIVE"]': { bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } },
                              '&[value="MONITOR"]': { bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } },
                              '&[value="INACTIVE"]': { bgcolor: '#64748b', '&:hover': { bgcolor: '#475569' } }
                          }
                      } 
                  }}
                >
                  <ToggleButton value="ACTIVE">Active</ToggleButton>
                  <ToggleButton value="MONITOR">Monitor</ToggleButton>
                  <ToggleButton value="INACTIVE">Inactive</ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <CustomInput
                  select label="Dependency Level *" fullWidth size="small"
                  value={responses[R_KEYS.LEVEL] || ""}
                  onChange={(e) => handleChange(R_KEYS.LEVEL, e.target.value)}
                >
                  {dependencyLevels.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: '0.85rem' }}>{opt.value}</MenuItem>
                  ))}
                </CustomInput>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <CustomDate
                  label="Next Review *" 
                  size="small"
                  value={responses[R_KEYS.NEXT_DATE] || ""}
                  onChange={(e) => {
                    const formatted = e.target.value;
                    handleChange(R_KEYS.NEXT_DATE, formatted);
                    initialDateSet.current = true;
                  }}
                />
              </Grid>

              <Grid size={12}>
                <CustomTextArea
                  label="Evaluation Summary *" rows={3} fullWidth
                  value={responses[R_KEYS.EVAL_SUMMARY] || ""}
                  onChange={(e) => handleChange(R_KEYS.EVAL_SUMMARY, e.target.value)}
                />
              </Grid>

              <Grid size={12}>
                <CustomTextArea
                  label="General Summary (Optional)" rows={2} fullWidth
                  value={responses[R_KEYS.GEN_SUMMARY] || ""}
                  onChange={(e) => handleChange(R_KEYS.GEN_SUMMARY, e.target.value)}
                />
              </Grid>
              
              <Grid size={6}>
                <CustomInput
                  label="Consent Person" fullWidth size="small"
                  value={responses[R_KEYS.CONSENT] || ""}
                  onChange={(e) => handleChange(R_KEYS.CONSENT, e.target.value)}
                />
              </Grid>
              
              <Grid size={6}>
                <CustomInput
                  label="Role / Method" fullWidth size="small"
                  value={responses[R_KEYS.ROLE] || ""}
                  onChange={(e) => handleChange(R_KEYS.ROLE, e.target.value)}
                />
              </Grid>

              <Grid size={12}>
                <Box sx={{ mt: 1, p: 2, border: '1px dashed #e2e8f0', borderRadius: '12px', bgcolor: '#f8fafc' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', mb: 1, display: 'block' }}>
                    AUTHORIZATION SIGNATURE
                  </Typography>
                  <SignatureFieldBase64
                    value={responses[R_KEYS.SIG] || ""}
                    onChange={async (blob) => handleChange(R_KEYS.SIG, blob ? await blobToBase64(blob) : null)}
                  />
                </Box>
              </Grid>

              <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  variant="contained" disableElevation
                  onClick={() => setOpenConfirm(true)}
                  disabled={mutation.isLoading}
                  sx={{ borderRadius: "50px", px: 4, bgcolor: '#0f172a', fontWeight: 800, textTransform: 'none' }}
                >
                  {mutation.isLoading ? "Pushing..." : "Update Review"}
                </Button>
              </Grid>
            </Grid>
          )}
        </Box>
      </Collapse>

      <ConfirmModal
        show={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        handleConfirm={handleFinalSubmit}
        title="Sync Review"
        body="Are you sure you want to push this review to the system?"
        isLoading={mutation.isLoading}
      />
    </Box>
  );
};

export default CarePlanReviewSection;